/**
 * As três réguas das vendas de anúncio, lado a lado no Hoje e no Tráfego (dono, 29/10/2026):
 *
 * - **Meta completa**: as compras que a Meta atribui na janela padrão da conta (7 dias depois do
 *   clique + 1 dia depois de ver), o número do Gerenciador;
 * - **Meta só clique**: as mesmas compras só pela janela de 7 dias do clique;
 * - **Kiwify pelo link**: pedidos aprovados do curso e do 20X com o canal de anúncio (`meta_ads`),
 *   pelo valor do produto (sem os juros do parcelamento).
 *
 * Cada uma com as compras, o valor e o ROAS (valor ÷ valor gasto na Meta, sem impostos). O ROAS do
 * topo continua o da Meta completa (regra do dono: venda de anúncio conta pela Meta). Régua sem dado
 * (o clique antes da sincronização nova): compras, valor e ROAS null, e a tela mostra "—".
 *
 * O modo ao vivo monta as réguas uma vez, no snapshot (`withAdRulers`); as duas telas leem com
 * `adRulersOf(snapshot)`.
 */
import type { DashboardSnapshot } from '@rota/contracts';

export interface AdRuler {
  purchases: number | null;
  valueCents: number | null;
  roas: number | null;
}

export interface AdRulers {
  /** Valor gasto na Meta no período e no recorte (sem impostos): o denominador dos três ROAS. */
  spendCents: number;
  /** Meta completa, com as compras do 20X separadas pelo valor (a base do "CPA do curso"). */
  full: AdRuler & { upsell: number | null };
  click: AdRuler;
  /** Kiwify pelo link, com as reembolsadas depois (já contadas nas compras) à parte. */
  kiwify: AdRuler & { refunded: number };
  /**
   * O recorte por campanha, conjunto ou anúncio: a janela de clique e o link não se separam por
   * campanha, e essas duas réguas ficam sem número.
   */
  entityCut?: boolean;
}

interface Counted {
  purchases: number;
  valueCents: number;
}

export function adRuler(c: Counted | null | undefined, spendCents: number): AdRuler {
  if (!c) return { purchases: null, valueCents: null, roas: null };
  return {
    purchases: c.purchases,
    valueCents: c.valueCents,
    roas: spendCents > 0 ? c.valueCents / spendCents : null,
  };
}

export function buildAdRulers(i: {
  spendCents: number;
  full: (Counted & { upsell?: number }) | null;
  click: Counted | null;
  kiwify: (Counted & { refunded: number }) | null;
  entityCut?: boolean;
}): AdRulers {
  return {
    spendCents: i.spendCents,
    full: { ...adRuler(i.full, i.spendCents), upsell: i.full ? (i.full.upsell ?? 0) : null },
    click: adRuler(i.click, i.spendCents),
    kiwify: { ...adRuler(i.kiwify, i.spendCents), refunded: i.kiwify?.refunded ?? 0 },
    ...(i.entityCut ? { entityCut: true } : {}),
  };
}

/** Campo do snapshot ao vivo, fora do contrato. */
type WithRulers = { adRulers?: AdRulers | null };

/** Grava as réguas no snapshot (modo ao vivo); null tira. */
export function withAdRulers(snap: DashboardSnapshot, r: AdRulers | null): void {
  if (r) (snap as DashboardSnapshot & WithRulers).adRulers = r;
  else delete (snap as DashboardSnapshot & WithRulers).adRulers;
}

/** As réguas do snapshot (só no modo ao vivo com a Meta ligada); na demonstração, null. */
export function adRulersOf(snap: DashboardSnapshot | null | undefined): AdRulers | null {
  return (snap as (DashboardSnapshot & WithRulers) | null | undefined)?.adRulers ?? null;
}

/**
 * CPA do curso (o mesmo no Hoje e no Resumo do Tráfego): valor gasto ÷ (compras da Meta − as do 20X,
 * separadas pelo valor). O teto do CPA já soma o 20X por comprador do curso, então o 20X não entra.
 */
export function courseCpa(r: AdRulers | null | undefined): {
  value: number | null;
  purchases: number;
  upsell: number;
} | null {
  if (!r || r.full.purchases == null) return null;
  const upsell = Math.min(r.full.upsell ?? 0, r.full.purchases);
  const course = r.full.purchases - upsell;
  return {
    value: r.spendCents > 0 && course > 0 ? Math.round(r.spendCents / course) : null,
    purchases: r.full.purchases,
    upsell,
  };
}
